import { useState } from 'react';

import { useExportEvents, BaseExportEvent } from '@/api/hooks/useExportEvents';
import { ExportProgressContent } from '@/components/molecules/ExportProgressModal';
import CustomModal from '@/components/molecules/Modals/CustomModal';

/**
 * Configuration for export endpoints
 */
export interface ExportConfig {
  /** Base endpoint for progress SSE */
  progressEndpoint: string;
}

/**
 * Props for GenericExportProgressModal
 */
interface GenericExportProgressModalProps<TEvent extends BaseExportEvent> {
  // Modal control
  isOpen: boolean;
  onClose: () => void;

  // Export configuration
  exportConfig: ExportConfig;

  // Display & behavior
  itemName: string;
  onDownload: () => Promise<void>;
  isCreating: boolean;
  error?: string;

  // Optional customization
  readyDescription?: string;
  downloadButtonText?: string;
  downloadButtonIcon?: React.ReactNode;
  onRetry?: () => void;
  creatingText?: string;
}

/**
 * Generic export progress modal that works for any export type.
 * Unifies ChronologyExportProgressModal, ThreadExportProgressModal, and DownloadZipModal.
 *
 * @example Chronology Export
 * ```tsx
 * <GenericExportProgressModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   exportConfig={{
 *     progressEndpoint: `/api/case/${caseId}/chronology/${chronologyId}/export-progress`,
 *   }}
 *   itemName={`Chronology "${title}"`}
 *   onDownload={handleDownload}
 *   isCreating={isCreating}
 *   error={error}
 * />
 * ```
 */
export const GenericExportProgressModal = <TEvent extends BaseExportEvent>({
  isOpen,
  onClose,
  exportConfig,
  itemName,
  onDownload,
  isCreating,
  error,
  readyDescription,
  downloadButtonText,
  downloadButtonIcon,
  onRetry,
  creatingText,
}: GenericExportProgressModalProps<TEvent>) => {
  const [exportReady, setExportReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [processedDocuments, setProcessedDocuments] = useState(0);

  useExportEvents<TEvent>({
    progressEndpoint: exportConfig.progressEndpoint,
    onEvent: (event) => {
      if (event.type.includes('progress')) {
        setProgress(event.progress || 0);
        if (event.totalDocuments) setTotalDocuments(event.totalDocuments);
        if (event.processedDocuments !== undefined) setProcessedDocuments(event.processedDocuments);
      } else if (event.type.includes('complete')) {
        setProgress(100);
        setExportReady(true);
      }
    },
    enabled: isOpen && !exportReady,
  });

  return (
    <CustomModal
      isOpen={isOpen}
      handleClose={onClose}
      maxWidth="40rem"
      content={
        <ExportProgressContent
          onDownload={onDownload}
          error={error}
          exportReady={exportReady}
          itemName={itemName}
          readyDescription={readyDescription}
          progress={progress}
          totalDocuments={totalDocuments}
          processedDocuments={processedDocuments}
          downloadButtonText={downloadButtonText}
          downloadButtonIcon={downloadButtonIcon}
          onRetry={onRetry}
          creatingText={creatingText}
        />
      }
    />
  );
};
